import React from 'react';
import { TbBrandWhatsapp, TbCaretUpFilled, TbCircleCheckFilled, TbLocationFilled, TbPhone } from 'react-icons/tb';
import { formatPhone } from '../../Utils/Formatters';
import Badge from './Badge';
import { formatAddressStore } from '../../Utils/Helpers';
import Button from '../Form/Button';
import { getIconByStoreType } from '../../Utils/Constants/Global';
import { useTranslation } from 'react-i18next';
import { LABEL_MENU, ORDER_TYPE_MENU } from '../../Utils/Constants/Service';

interface CardStoreListGoogleMapsProps {
    store: any;
    onCardClick: (store: any) => void;
    enableCardClick?: boolean;
    showServices?: boolean;
    isCollapsed?: boolean;
    className?: string;
    isOpen: boolean;
    isCurrent: boolean;
    isCurrentOrderType: (orderTypeNameSelected: string) => boolean;
    showInfo: boolean;
    activeServices: any[];
    onToggleShowInfo: () => void;
    onClickService: (orderTypeSelected: any) => void;
}

const CardStoreListGoogleMaps = ({
    store,
    onCardClick,
    enableCardClick = true,
    showServices = false,
    isCollapsed = false,
    className,
    isOpen,
    isCurrent,
    isCurrentOrderType,
    showInfo,
    activeServices,
    onToggleShowInfo,
    onClickService
}: CardStoreListGoogleMapsProps) => {

    const { t } = useTranslation();

    return (
        <>
            <section className={`
                    grid w-full justify-between rounded-xl p-0.5 gap-4
                    bg-white shadow-md shadow-gray-100
                    ${enableCardClick && 'cursor-pointer'}
                    ${className}
                `}
                onClick={() => enableCardClick ? onCardClick(store) : {}}
                >
                <div className='flex flex-row gap-3'>

                    <div className='grid text-left w-full gap-1.5 text-sm'>


                        <div className='grid gap-1'>

                            {!showInfo &&
                                <>
                                    <div className='text-gray-500 text-sm leading-none'>
                                        { formatAddressStore(store) }
                                    </div>

                                    {store.distance &&
                                        <div className='flex items-center flex-wrap gap-x-0.5 text-nowrap font-medium text-gray-500'>
                                            <TbLocationFilled /> { store.distance } de distância
                                        </div>
                                    }

                                    {(store.phone || store.phone_additional || store.whatsapp_number) &&
                                        <div className='flex items-center flex-wrap gap-x-2 text-gray-500'>
                                            {store.phone && (store.phone !== store.whatsapp_number) && <span className='flex gap-0.5 items-center text-nowrap'><TbPhone /> { formatPhone(store.phone) }</span>}
                                            { store.phone_additional && <span className='flex gap-0.5 items-center text-nowrap'><TbPhone /> { formatPhone(store.phone_additional) }</span>  }
                                            { store.whatsapp_number && <span className='flex gap-0.5 items-center text-nowrap'><TbBrandWhatsapp /> { formatPhone(store.whatsapp_number) }</span> }
                                        </div>
                                    }
                                </>
                            }
                        </div>

                        {store.types && store.types.length > 0 &&
                            <div className='flex items-center flex-wrap gap-1 opacity-75 h-full'>
                                {store?.types.map((type: any, index: number) =>
                                    <Badge key={index} type='tag'>{getIconByStoreType(type.type,  12)} { type.name }</Badge>
                                )}
                            </div>
                        }

                        <div className='flex items-center gap-1 mt-0.5'>
                            <Badge outline className={`text-xs normal-case font-semibold bg-transparent ${isOpen ? 'text-shopfood-success' : 'text-shopfood-error'}`}>
                                {isOpen ? t('layout.store_opened_label') : t('layout.store_closed_label')}
                            </Badge>
                            {isCurrent &&
                                <Badge outline className='text-xs normal-case gap-0.5'>
                                    <TbCircleCheckFilled className='shrink-0' />
                                    {t('layout.store_list.current_label')}
                                </Badge>
                            }
                        </div>

                        {showServices &&
                            <>
                                <div className="flex flex-wrap gap-1.5 self-end border-t border-gray-100 border-dashed pt-2">
                                    {activeServices.length === 0
                                    ?   <Button
                                            onClick={() => onClickService({order_type: ORDER_TYPE_MENU, order_type_name: ORDER_TYPE_MENU })}
                                            className="relative h-8"
                                            >
                                            <span className="text-xs leading-none text-nowrap font-medium first-letter:uppercase lowercase">
                                                {t('layout.store_list.button_open_menu_label', { labelMenu: LABEL_MENU })}
                                            </span>
                                            {isCurrent && isCurrentOrderType(ORDER_TYPE_MENU) &&
                                                <TbCircleCheckFilled size={15} className='absolute -right-0.5 -top-0.5 animate-fade-in' />
                                            }
                                        </Button>
                                    :   activeServices.map((service: any) => {
                                        return (
                                            <Button
                                                key={service.id}
                                                onClick={() => onClickService(service)}
                                                className="relative h-8"
                                                >
                                                <span className="text-xs leading-none text-nowrap font-medium">
                                                    {service.order_type_name_custom}
                                                </span>
                                                {isCurrent && isCurrentOrderType(service.order_type_name) &&
                                                    <TbCircleCheckFilled size={15} className='absolute -right-0.5 -top-1 animate-fade-in' />
                                                }
                                            </Button>
                                        )}
                                    )}
                                </div>
                            </>
                        }

                        {isCollapsed &&
                            <button onClick={onToggleShowInfo} className={`flex items-center gap-0.5 h-full text-xs leading-none font-medium text-nowrap text-gray-400`}>
                                {showInfo ? t('layout.store_list.show_info_label') : t('layout.store_list.hide_info_label')}
                                <span><TbCaretUpFilled className={`transition-transform duration-300 ${showInfo ? '-rotate-180' : ''}`} /></span>
                            </button>
                        }

                    </div>
                </div>

            </section>

        </>
    );
}

export default CardStoreListGoogleMaps;
